import React, { useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { ChatMessage } from '../../lib/types';
import { Loader2, User, Sparkles } from 'lucide-react';
interface MessageListProps {
  messages: ChatMessage[];
  isLoading?: boolean;
}
export function MessageList({
  messages,
  isLoading
}: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);
  if (messages.length === 0 && !isLoading) {
    return <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-muted-foreground" data-id="element-97">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4" data-id="element-98">
          <MessageSquareIcon size={32} className="text-primary" data-id="element-99" />
        </div>
        <h3 className="font-medium text-lg mb-2" data-id="element-100">Start the conversation</h3>
        <p className="text-sm max-w-xs" data-id="element-101">
          Ask questions about the video, get summaries, or explore key topics.
        </p>
      </div>;
  }
  return <div className="flex-1 overflow-y-auto p-4 space-y-6" data-id="element-102">
      {messages.map(message => <div key={message.id} className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-103">
          {message.role === 'assistant' && <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-104">
              <Sparkles size={14} className="text-primary" data-id="element-105" />
            </div>}

          <div className={`max-w-[85%] md:max-w-[75%] rounded-2xl px-4 py-3 ${message.role === 'user' ? 'bg-primary text-primary-foreground rounded-tr-sm' : 'bg-secondary text-secondary-foreground rounded-tl-sm'}`} data-id="element-106">
            {message.role === 'assistant' ? <div className="text-sm leading-relaxed prose prose-sm dark:prose-invert max-w-none" data-id="element-107">
                <ReactMarkdown components={{
            p: ({
              children
            }) => <p className="mb-2 last:mb-0" data-id="element-109">{children}</p>,
            ul: ({
              children
            }) => <ul className="list-disc list-inside space-y-1 my-2" data-id="element-110">
                        {children}
                      </ul>,
            ol: ({
              children
            }) => <ol className="list-decimal list-inside space-y-1 my-2" data-id="element-111">
                        {children}
                      </ol>,
            li: ({
              children
            }) => <li className="ml-2" data-id="element-112">{children}</li>,
            code: ({
              children
            }) => <code className="bg-black/10 dark:bg-white/10 px-1.5 py-0.5 rounded text-xs font-mono" data-id="element-113">
                        {children}
                      </code>
          }} data-id="element-108">
                  {message.content}
                </ReactMarkdown>
              </div> : <p className="text-sm whitespace-pre-wrap" data-id="element-114">{message.content}</p>}

            <div className={`text-[10px] mt-1 opacity-70 ${message.role === 'user' ? 'text-right' : 'text-left'}`} data-id="element-115">
              {new Date(message.created_at).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          })}
            </div>
          </div>

          {message.role === 'user' && <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-1" data-id="element-116">
              <User size={14} className="text-primary-foreground" data-id="element-117" />
            </div>}
        </div>)}

      {isLoading && <div className="flex gap-3 justify-start" data-id="element-118">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0" data-id="element-119">
            <Sparkles size={14} className="text-primary" data-id="element-120" />
          </div>
          <div className="bg-secondary text-secondary-foreground rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-2" data-id="element-121">
            <Loader2 size={16} className="animate-spin" data-id="element-122" />
            <span className="text-xs text-muted-foreground" data-id="element-123">Thinking...</span>
          </div>
        </div>}

      <div ref={messagesEndRef} data-id="element-124" />
    </div>;
}
function MessageSquareIcon({
  size,
  className
}: {
  size: number;
  className?: string;
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-125">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" data-id="element-126" />
    </svg>;
}